/* EBITDA widget for the Financial dashboard — powered by the per-order
 * EBITDA aggregator (`/api/admin/tax-center/ebitda-per-book`) plus the
 * subscription-level EBITDA / EBITDAT snapshot (`/api/admin/tax-center/ebitda`).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Loader2, RefreshCw } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function usd(cents) {
  const v = (cents || 0) / 100;
  return v < 0 ? `−$${Math.abs(v).toFixed(2)}` : `$${v.toFixed(2)}`;
}

export default function EbitdaFinancialWidget() {
  const [book, setBook] = useState(null);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [b, s] = await Promise.all([
        http.get('/admin/tax-center/ebitda-per-book'),
        http.get('/admin/tax-center/ebitda'),
      ]);
      setBook(b.data);
      setSnap(s.data);
    } catch (_e) {
      /* silent — non-critical */
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (!book && !snap && !busy) return null;

  return (
    <div
      data-testid="financial-ebitda-widget"
      className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <div className="text-[13px] font-semibold tracking-tight">Live EBITDA · from every book sale</div>
        </div>
        <button
          type="button" onClick={load} disabled={busy}
          data-testid="financial-ebitda-refresh"
          className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Metric label="Book orders"  value={book?.orders ?? 0} />
        <Metric label="Book gross"   value={usd(book?.gross_cents)} />
        <Metric label="Book COGS"    value={usd(book?.cogs_cents)} />
        <Metric label="Book EBITDA"  value={usd(book?.ebitda_cents)} tone={book?.ebitda_cents < 0 ? 'bad' : 'good'} />
        {snap && (
          <>
            <Metric label="Total revenue" value={`$${snap.revenue_usd?.toFixed(2) ?? '0.00'}`} />
            <Metric label="Company EBITDA" value={`$${snap.ebitda_usd?.toFixed(2) ?? '0.00'}`} tone="good" />
            <Metric label="EBITDAT (post-tax)" value={`$${snap.ebitdat_usd?.toFixed(2) ?? '0.00'}`} tone="good" />
            <Metric label="Margin %" value={`${snap.margin_pct ?? 0}%`} />
          </>
        )}
      </div>

      {book?.worst_books?.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Least profitable titles</div>
          <table className="w-full text-[11.5px]" data-testid="financial-ebitda-worst">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="py-1">Book</th><th>Orders</th><th className="text-right">Gross</th><th className="text-right">EBITDA</th>
              </tr>
            </thead>
            <tbody>
              {book.worst_books.slice(0, 5).map((r) => (
                <tr key={r.book_id} className="border-b border-border/60">
                  <td className="truncate py-1">{r.book_id}</td>
                  <td>{r.orders}</td>
                  <td className="text-right">{usd(r.gross_cents)}</td>
                  <td className={`text-right ${r.ebitda_cents < 0 ? 'text-red-500' : ''}`}>{usd(r.ebitda_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  const toneCls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-300'
    : tone === 'bad' ? 'text-red-500'
    : '';
  return (
    <div className="rounded-md border border-border bg-panel-header/40 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-[15px] font-semibold tracking-tight ${toneCls}`}>{value}</div>
    </div>
  );
}
